import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_agreement,
  fetch_draft,
  fetch_my_kyc_docs,
  fetch_my_photos,
  fetch_saved_address,
  fetch_saved_payout,
  fetch_saved_basic_info,
  save_address,
  save_basic_info,
  save_payout,
  save_terms,
  start_onboarding,
  submit_onboarding,
  upload_kyc,
  upload_photo,
} from './api';
import type {
  AddressRequest,
  BasicInfoRequest,
  DraftSummary,
  KycDocumentType,
  PayoutRequest,
  StartRequest,
  StepResponse,
  TermsRequest,
  VendorPhotoType,
} from './types';

const DRAFT_KEY = ['onboarding', 'draft'] as const;

export function use_draft() {
  return useQuery({
    queryKey: DRAFT_KEY,
    queryFn: fetch_draft,
    staleTime: 0,
    retry: 0,
  });
}

const SAVED_BASIC_INFO_KEY = ['onboarding', 'basic-info'] as const;
const SAVED_ADDRESS_KEY = ['onboarding', 'address'] as const;
const SAVED_PAYOUT_KEY = ['onboarding', 'payout'] as const;

export function use_saved_basic_info(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: SAVED_BASIC_INFO_KEY,
    queryFn: fetch_saved_basic_info,
    staleTime: 0,
    retry: 0,
    enabled: options?.enabled ?? true,
  });
}

export function use_saved_address(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: SAVED_ADDRESS_KEY,
    queryFn: fetch_saved_address,
    staleTime: 0,
    retry: 0,
    enabled: options?.enabled ?? true,
  });
}

export function use_saved_payout(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: SAVED_PAYOUT_KEY,
    queryFn: fetch_saved_payout,
    staleTime: 0,
    retry: 0,
    enabled: options?.enabled ?? true,
  });
}

function merge_step(old: DraftSummary | null | undefined, step: StepResponse): DraftSummary | null | undefined {
  if (!old) return old;
  return {
    ...old,
    current_step: step.current_step,
    basic_info_completed: step.basic_info_completed,
    address_completed: step.address_completed,
    payout_completed: step.payout_completed,
    payout_skipped: step.payout_skipped,
    terms_completed: step.terms_completed,
  };
}

export function use_start_onboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartRequest) => start_onboarding(input),
    onSuccess: (data) => {
      // Backend returns full draft summary — write it straight into the cache
      // so the next page renders without waiting for a refetch (avoids the
      // momentary `data === null` window that triggers redirect-to-start).
      qc.setQueryData(DRAFT_KEY, data);
    },
  });
}

export function use_save_basic_info() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BasicInfoRequest) => save_basic_info(input),
    onSuccess: (step) => {
      qc.setQueryData(DRAFT_KEY, (old: DraftSummary | null | undefined) => merge_step(old, step));
      // Refresh saved-data cache so revisiting the page sees the latest values.
      qc.invalidateQueries({ queryKey: SAVED_BASIC_INFO_KEY });
    },
  });
}

export function use_save_address() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddressRequest) => save_address(input),
    onSuccess: (step) => {
      qc.setQueryData(DRAFT_KEY, (old: DraftSummary | null | undefined) => merge_step(old, step));
      qc.invalidateQueries({ queryKey: SAVED_ADDRESS_KEY });
    },
  });
}

export function use_save_payout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PayoutRequest) => save_payout(input),
    onSuccess: (step) => {
      qc.setQueryData(DRAFT_KEY, (old: DraftSummary | null | undefined) => merge_step(old, step));
      qc.invalidateQueries({ queryKey: SAVED_PAYOUT_KEY });
    },
  });
}

export function use_save_terms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TermsRequest) => save_terms(input),
    onSuccess: (step) => {
      qc.setQueryData(DRAFT_KEY, (old: DraftSummary | null | undefined) => merge_step(old, step));
    },
  });
}

export function use_submit_onboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => submit_onboarding(),
    onSuccess: (res) => {
      // Mark draft as submitted in the cache so the success screen renders
      // without a refetch.
      qc.setQueryData(DRAFT_KEY, (old: DraftSummary | null | undefined) => {
        if (!old) return old;
        return { ...old, submitted_at: res.submitted_at, current_step: 'SUBMITTED' as const };
      });
    },
  });
}

const MY_KYC_KEY = ['uploads', 'kyc-docs'] as const;
const MY_PHOTOS_KEY = ['uploads', 'photos'] as const;

/** In-flight uploads for this phone — drives uploads-page prepopulation. */
export function use_my_kyc_docs(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: MY_KYC_KEY,
    queryFn: fetch_my_kyc_docs,
    staleTime: 0,
    retry: 0,
    enabled: options?.enabled ?? true,
  });
}

export function use_my_photos(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: MY_PHOTOS_KEY,
    queryFn: fetch_my_photos,
    staleTime: 0,
    retry: 0,
    enabled: options?.enabled ?? true,
  });
}

export function use_upload_kyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { type: KycDocumentType; file: File }) => upload_kyc(input),
    onSuccess: () => {
      // kyc_doc_count on the draft just changed — refresh the rail tick +
      // the uploads list.
      qc.invalidateQueries({ queryKey: DRAFT_KEY });
      qc.invalidateQueries({ queryKey: MY_KYC_KEY });
    },
  });
}

export function use_upload_photo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { type: VendorPhotoType; file: File; display_order?: number }) =>
      upload_photo(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DRAFT_KEY });
      qc.invalidateQueries({ queryKey: MY_PHOTOS_KEY });
    },
  });
}

export function use_agreement() {
  return useQuery({ queryKey: ['onboarding', 'agreement'] as const, queryFn: fetch_agreement, staleTime: Infinity });
}
